import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);

  if (!article) {
    return (
      <div className="py-20 px-4 text-center">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
          <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
        </div>
        <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
        <p className="text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被删除</p>
        <Link
          to="/"
          className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors inline-block whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '爱情岛论坛亚洲',
    },
    image: article.image,
    url: `https://www.aiqingdao.com/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.aiqingdao.com/article/${article.slug}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.aiqingdao.com/' },
              { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.aiqingdao.com/category/${article.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: article.title },
            ],
          }),
        }}
      />

      <article className="py-8 md:py-10 px-4 md:px-6">
        <div className="w-full max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
              >
                {article.category}
              </Link>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1.5">
                <span className="w-6 h-6 flex items-center justify-center rounded-full overflow-hidden bg-background-200">
                  <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                </span>
                {article.author}
              </span>
              <span>|</span>
              <span>{article.date}</span>
              <span>|</span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {article.views.toLocaleString()} 阅读
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <div className="w-full h-[300px] md:h-[400px] rounded-xl overflow-hidden mb-8">
            <img
              src={article.image}
              alt={article.title}
              title={`${article.title} - 爱情岛论坛亚洲`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-custom text-foreground-900 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Article Footer */}
          <div className="mt-10 pt-8 border-t border-background-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 text-sm transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line"></i> 点赞 ({article.likes})
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 text-sm transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-bookmark-line"></i> 收藏
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 text-sm transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
              <Link
                to={`/category/${article.categorySlug}`}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 whitespace-nowrap"
              >
                返回{article.category} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="mt-12 pt-8 border-t border-background-200">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group flex flex-col rounded-xl bg-background-100 hover:bg-background-200 transition-colors overflow-hidden cursor-pointer"
                  >
                    <div className="h-[140px] overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        title={`${related.title} - 爱情岛论坛亚洲`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{related.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}